import ModalImage from "react-modal-image";
import Title from "./Title";
import { Expand, Gamepad2, MessageCircle, Newspaper, Pen } from "lucide-react";

const features = [
    {
        title: "Real-time nieuwsupdates",
        description:
            "De bot haalt automatisch de laatste nieuwsupdates van de NPO website en plaatst deze in een kort berichtje op de Discord server. Alles wordt gedeeld, maar gebruikers kunnen per categorie kiezen welke notificaties ze willen ontvangen.",
        icon: <Newspaper size={32} color="#FB923C" />,
    },
    {
        title: "Minigames",
        description:
            "De bot heeft een minigame die de gebruikers kunnen spelen, namelijk Lingo. De minigame verwerkt op een speelse manier de publieke waarden van de NPO. Win je het spel? Dan verdien je een punt op de NPO leaderboard!",
        icon: <Gamepad2 size={32} color="#FB923C" />,
    },
    {
        title: "Interactieve discussies",
        description:
            'De bot stimuleert discussies over het nieuws door middel van de "vraag van de dag" en threads, die discussies aanwakkeren. Moderatie wordt geregeld door de serverbeheerder zelf.',
        icon: <MessageCircle size={32} color="#FB923C" />,
    },
    {
        title: "Personalisatie",
        description:
            "De bot kan in elke Discord server. Er hoeft dus geen centrale NPO server te zijn. Je kunt de bot aanpassen aan je eigen interesses en behoeften. Kies bijvoorbeeld van welke nieuwsartikelen je notificaties wilt ontvangen in het rollen kanaal.",
        icon: <Pen size={32} color="#FB923C" />,
    },
];

export default function EndProductSection() {
    return (
        <section className="py-10 sm:py-16">
            <Title
                title={"De NPO Discord Bot"}
                linkableID={"eindproduct"}
                subtitle={"Wat wij na 8 weken hebben opgeleverd"}
                className={"!pb-0 sm:!pb-20"}
            />
            <p className="mx-auto max-w-4xl mb-7 md:mb-20">
                Discord is een zogenoemde "Dark Social". Dit betekent dat het een
                platform is waarbij de content niet openbaar is en niet geïndexeerd
                wordt door zoekmachines. Dit maakt het lastig voor de NPO om te weten
                wat er speelt onder de jongeren. Er zitten veel mensen op Discord,
                ondanks dat het een van de kleinere social is. In 2022 zaten er al
                <a
                    href="https://www.marketingfacts.nl/berichten/social-media-in-nederland-2022/"
                    className="font-bold"
                >
                    {" "}
                    1.4 miljoen mensen {" "}
                </a>
                op het platform. Na een enquete blijkt dat 80% van de ondervraagden
                jongeren Discord gebruikt en
                <strong> 60% aangeeft dat ze Discord dagelijks gebruiken*</strong>.
                <br /> <br />
                Hier zagen wij een kans voor de NPO, omdat zij nog geen Discord
                integratie hebben. Wij hebben een Discord bot ontwikkeld die jongeren op
                een nieuwe en interactieve manier in contact brengt met de NPO en het
                nieuws. De bot is makkelijk te integreren in elke Discord server en
                biedt real-time nieuwsupdates, minigames en interactieve discussies.
                <br />
                <br />
                <em>*De ondervraagden zijn veelal studenten die een technische studie doen.</em>
            </p>
            <div className="flex flex-col justify-center mb-10">
                <h3 className="text-xl font-medium tracking-tight text-center text-gray-900 sm:text-[2.25rem] mb-7 md:mb-14">
                    Features
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
                    {features.map((feature) => (
                        <div className="py-14 px-4 border border-1 rounded-lg bg-white border-gray-200/80">
                            <div className="flex justify-center mb-4">{feature.icon}</div>
                            <h3 className="text-lg font-medium tracking-tight text-center text-gray-900 sm:text-[1.5rem] mb-4">
                                {feature.title}
                            </h3>
                            <p className="text-center text-gray-600 text-base">
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>
                <div className="max-w-7xl w-full mx-auto my-10 grid grid-cols-2 gap-8">
                    <figure className="relative w-full h-full">
                        <ModalImage
                            small={"./features-discord.jpg"}

                            large={"./features-discord.jpg"}
                            alt="Features van de Discord bot"
                            className="rounded-lg object-cover w-full hover:opacity-70 mb-2 border border-gray-100"
                        />
                        <span className="absolute top-2 right-2 p-2 bg-white rounded-lg text-gray-900 text-xs font-semibold pointer-events-none"> <Expand size={24} color="#fa7900" strokeWidth={2} absoluteStrokeWidth />  </span>
                        <figcaption className="text-left text-gray-600 text-sm italic">
                            Een overzicht van de features van de Discord bot. Lingo, de vraag van de dag met feedback en een leaderboard.
                        </figcaption>

                    </figure>
                    <figure className="relative w-full h-full">
                        <ModalImage
                            small={"features-news.jpg"}
                            large={"features-news.jpg"}
                            alt="Features van de Discord bot"
                            className="rounded-lg object-cover w-full hover:opacity-70 mb-2 border border-gray-100"
                        />
                        <span className="absolute top-2 right-2 p-2 bg-white rounded-lg text-gray-900 text-xs font-semibold pointer-events-none"> <Expand size={24} color="#fa7900" strokeWidth={2} absoluteStrokeWidth />  </span>
                        <figcaption className="text-left text-gray-600 text-sm italic">
                            Een overzicht van de features van de Discord bot. Nieuwsnotificaties, real-time updates en een notificatie van jouw favoriete show.
                        </figcaption>
                    </figure>

                </div>
            </div>
            <div className="flex flex-col justify-center mb-10">
                <h3 className="text-xl font-medium tracking-tight text-center text-gray-900 sm:text-[2.25rem] mb-7 md:mb-14">
                    Redactie
                </h3>
                <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto mb-10">
                    <div>
                        <figure className="relative w-full h-full">
                            <ModalImage
                                small={"./prototype-dashboard.jpg"}
                                large={"./prototype-dashboard.jpg"}
                                alt="Prototype dashboard"
                                className="rounded-lg object-cover w-full hover:opacity-70 mb-2 border border-gray-100"
                            />
                            <span className="absolute top-2 right-2 p-2 bg-white rounded-lg text-gray-900 text-xs font-semibold pointer-events-none"> <Expand size={24} color="#fa7900" strokeWidth={2} absoluteStrokeWidth />  </span>
                            <figcaption className="text-left text-gray-600 text-sm italic">
                                Flow van redactie voor het pushen van exclusieve content of
                                andere berichten. No-code, dus iedereen kan er makkelijk mee
                                opweg.
                            </figcaption>
                        </figure>
                    </div>
                    <div>
                        De redactie kan makkelijk content pushen naar <strong>alle</strong>{" "}
                        Discord servers waar de bot te vinden is op een aantrekkelijke
                        manier via een dashboard. Dit kan gaan om exclusieve content of
                        andere berichten. De vraag van de dag wordt automatisch ingesteld
                        door AI, die aan de hand van de actualiteiten een vraag stelt.
                        <br /> <br />
                        De real-time nieuwsupdates worden door middel van code van de
                        website gehaald en in een kort berichtje geplaatst. Hier hoeft de
                        redactie niks voor te doen. Het programmeren van de Discord bot is
                        redelijk eenvoudig. Er zijn veel populaire packages die het
                        makkelijk maken om games toe te voegen en met de Discord API te
                        communiceren.
                        <br /> <br />
                        De moderatie van de discussies wordt door de beheerder van de
                        community waar de bot in zit gedaan.
                    </div>
                </div>
            </div>
            <div className="flex flex-col justify-center">
                <h3 className="text-xl font-medium tracking-tight text-center text-gray-900 sm:text-[2.25rem] mb-7 md:mb-14">
                    Vervolgstappen
                </h3>
                <div className="lg:grid-cols-2 grid max-w-7xl mx-auto gap-8">
                    <div>
                        <figure className="relative w-full h-full">
                            <ModalImage
                                small={"./feedbackdiscord.png"}
                                large={"./feedbackdiscord.png"}
                                alt="Prototype"
                                className="rounded-lg  object-cover w-full hover:opacity-70 mb-2 border border-gray-100"
                            />
                            <span className="absolute top-2 right-2 p-2 bg-white rounded-lg text-gray-900 text-xs font-semibold pointer-events-none"> <Expand size={24} color="#fa7900" strokeWidth={2} absoluteStrokeWidth />  </span>
                            <figcaption className="text-left text-gray-600 text-sm italic">
                                Foto van emojis die
                                gebruikers kunnen sturen onder een nieuwsbericht waar
                                statistieken over kunnen worden verzameld. Deze feedback wordt
                                op het dashboard weergegeven.
                            </figcaption>
                        </figure>
                    </div>
                    <div>
                        <p>
                            Wij denken dat de bot een goede basis biedt voor de NPO en dat wij
                            de waarde van de bot goed hebben kunnen demonstreren met dit
                            prototype. Desondanks zijn er nog dingen die verbeterd kunnen
                            worden. <br /> <br />
                            <strong>
                                Wij raden aan om de volgende dingen nog toe te voegen:
                            </strong>{" "}
                            <br />
                            <br />
                            <ul className="list-disc list-inside space-y-2">
                                <li>
                                    Integraties met tv programma’s zoals Wie is de Mol, Slimste
                                    Mens.
                                </li>
                                <li>Meer minigames toevoegen aan de bot, zoals slimste mens of
                                    galgje.
                                </li>
                                <li>
                                    Link met vraag van de dag met actueel nieuws en NPO kennis.
                                </li>
                                <li>
                                    Gebruik maken van bestaande (RSS) APIs in plaats van
                                    webscrapen. Dat is betrouwbaarder en sneller
                                </li>
                                <li> TV gids + notificaties toevoegen (bestaande APIs) </li>
                            </ul>
                            <br />
                            Daarnaast is het aan te raden om iets toe te voegen waardoor de
                            statistieken van de discussies kunnen worden bekeken. Dit kan
                            bijvoorbeeld gedaan worden door te kijken naar het aantal reacties
                            (emojis) op een nieuwsbericht, of door te kijken hoeveel
                            discussies er zijn geopend in de thread van het nieuwsbericht.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
